import { designLevel, hashSeed, Rng, type DesignSpec } from "@enjoyment/core";

export const PAD_COUNT = 4;
export const ROUNDS_PER_LEVEL = 3;
/** Reverse echo starts here, on its own levels (every 3rd), never as a surprise mid-level. */
export const REVERSE_FROM = 6;
export const MAX_START = 9;

export type EchoPhase = "ready" | "showing" | "input" | "mistake" | "success" | "won";

/** Level N, deterministic: a master sequence that grows by one symbol each round. */
export interface LevelSpec {
  n: number;
  /** Symbols in round 1 (round r has start + r - 1). */
  start: number;
  rounds: number;
  /** Recall the sequence from its last symbol to its first. */
  reverse: boolean;
  /** Playback gap per symbol (ms); a little quicker as levels climb. No timer on input. */
  tempo: number;
}

export interface EchoState {
  spec: LevelSpec;
  round: number;
  pattern: number[];
  typed: number[];
  phase: EchoPhase;
  /** Mistakes in this attempt (each one is a free replay). */
  errors: number;
}

/** Fastest playback gap the ramp reaches (level 31 on). */
export const MIN_TEMPO = 450;

/**
 * Chapter design (levels 21+, @enjoyment/core designer.ts): sequence length and tempo come from one of the levels
 * 7–31 of the ramp below (warm-ups, build-ups, hard levels with breathers, a finale). Reverse echo moves from every
 * third level to the chapter's hard levels and its finale. Twists: a chapter of reverse echo (every level but the
 * breathers), or one played at the ramp's fastest tempo. The sequence itself still follows the real level number.
 */
export const DESIGN: DesignSpec = { game: "yanki", floor: 7, ceiling: 31, twists: ["quick", "reverse"] };

/** Hand-tuned levels: every third level from REVERSE_FROM on. */
const rampReverse = (n: number): boolean => n >= REVERSE_FROM && n % 3 === 0;

export function isReverseLevel(n: number): boolean {
  const d = designLevel(DESIGN, n);
  if (!d) return rampReverse(n);
  return d.role === "hard" || d.role === "boss" || (d.twist === "reverse" && d.role !== "breather");
}

export function levelSpec(levelNumber: number): LevelSpec {
  const n = Math.max(1, Math.floor(levelNumber));
  const d = designLevel(DESIGN, n);
  if (!d) return rampSpec(n);
  const base = rampSpec(d.effective);
  return { ...base, n, reverse: isReverseLevel(n), tempo: d.twist === "quick" ? MIN_TEMPO : base.tempo };
}

/** The hand-tuned ramp (levels 1–20, and the difficulty scale the designer draws from). */
function rampSpec(n: number): LevelSpec {
  return {
    n,
    start: Math.min(MAX_START, 3 + Math.floor((n - 1) / 3)),
    rounds: ROUNDS_PER_LEVEL,
    reverse: rampReverse(n),
    tempo: Math.max(MIN_TEMPO, 690 - (n - 1) * 8),
  };
}

export function sequenceLength(spec: LevelSpec, round: number): number {
  return spec.start + Math.max(1, Math.floor(round)) - 1;
}

/** One master sequence per level; each round reveals one more symbol. No runtime randomness is used. */
export function patternFor(spec: LevelSpec, round: number): number[] {
  const rng = new Rng(hashSeed(`yanki:level:${spec.n}`));
  return Array.from({ length: sequenceLength(spec, round) }, () => rng.int(PAD_COUNT));
}

export function responseFor(state: Pick<EchoState, "spec" | "pattern">): number[] {
  return state.spec.reverse ? [...state.pattern].reverse() : [...state.pattern];
}

/** The player always hears the master sequence; reverse levels reverse only the recall order. */
export function playbackFor(state: Pick<EchoState, "pattern">): number[] {
  return [...state.pattern];
}

export function createGame(levelNumber: number): EchoState {
  const spec = levelSpec(levelNumber);
  return { spec, round: 1, pattern: patternFor(spec, 1), typed: [], phase: "ready", errors: 0 };
}

export function startShowing(state: EchoState): EchoState {
  if (state.phase !== "ready" && state.phase !== "mistake" && state.phase !== "input") return state;
  return { ...state, typed: [], phase: "showing" };
}

export function startInput(state: EchoState): EchoState {
  if (state.phase !== "showing") return state;
  return { ...state, typed: [], phase: "input" };
}

export function enterPad(state: EchoState, pad: number): EchoState {
  if (state.phase !== "input" || !Number.isInteger(pad) || pad < 0 || pad >= PAD_COUNT) return state;
  const expected = responseFor(state);
  if (pad !== expected[state.typed.length]) return { ...state, phase: "mistake", errors: state.errors + 1 };
  const typed = [...state.typed, pad];
  if (typed.length < expected.length) return { ...state, typed };
  return { ...state, typed, phase: state.round >= state.spec.rounds ? "won" : "success" };
}

export function nextRound(state: EchoState): EchoState {
  if (state.phase !== "success") return state;
  const round = state.round + 1;
  return { ...state, round, pattern: patternFor(state.spec, round), typed: [], phase: "ready" };
}

/** Stars from free replays used: none → 3★, one or two → 2★. Only play decides (H3). */
export function starsFor(errors: number): 1 | 2 | 3 {
  return errors <= 0 ? 3 : errors <= 2 ? 2 : 1;
}

/** H4: three mistakes on a level offer a free skip (never sold). */
export const SKIP_AFTER_MISTAKES = 3;
export const canSkip = (mistakes: number): boolean => mistakes >= SKIP_AFTER_MISTAKES;
